if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) {
          return;
        }
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (this.submitButton) {
          this.submitButtonText = this.submitButton.querySelector('span');
          if (document.querySelector('cart-drawer')) {
            this.submitButton.setAttribute('aria-haspopup', 'dialog');
          }
        }
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.variantIdentities = new Map();
        this.registerVariantIdentities();
      }

      registerVariantIdentities() {
        const node = this.querySelector('[data-bd-variant-identities]');
        if (!node) return;
        try {
          const records = JSON.parse(node.textContent || '[]');
          records.forEach((record) => {
            const variantId = String(record?.variantId || '').trim();
            if (!variantId) return;
            this.variantIdentities.set(variantId, String(record?.sku || '').trim().toUpperCase());
          });
          if (window.BizDecksCartIdentityRegister) window.BizDecksCartIdentityRegister(records);
        } catch (error) {
          console.warn('BizDecks variant identity registry skipped.', error);
        }
      }

      getVariantIdentity(variantId, formData) {
        const id = String(variantId || '').trim();
        const mappedSku = this.variantIdentities.get(id) || '';
        const formSku = String(formData.get('properties[_bd_canonical_sku]') || '').trim().toUpperCase();
        const skuInput = this.form.querySelector('[data-bd-canonical-sku-input]');
        const formSkuVariantId = String(skuInput?.dataset.bdVariantId || '').trim();
        const safeFormSku = formSkuVariantId === id ? formSku : '';
        return { variantId: id, sku: mappedSku || safeFormSku };
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitting || !this.submitButton || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.submitting = true;
        this.error = false;
        let confirmedAdd = false;
        let actionRef = null;
        let mutationSent = false;
        let receivedRejection = false;
        const spinner = this.querySelector('.loading-overlay__spinner');
        this.safeCartMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        spinner?.classList.remove('hidden');
        try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const variantIdentity = this.getVariantIdentity(formData.get('id'), formData);
        const registeredGiftCard = this.querySelector('[data-bd-variant-identities]')?.dataset.bdGiftCard === 'true';
        const isGiftCard = this.dataset.bdGiftCard === 'true' || registeredGiftCard;
        if (!isGiftCard) {
          formData.delete('properties[_bd_canonical_sku]');
          formData.delete('properties[_bd_canonical_variant_id]');
          if (variantIdentity.sku) formData.set('properties[_bd_canonical_sku]', variantIdentity.sku);
          else if (variantIdentity.variantId) formData.set('properties[_bd_canonical_variant_id]', variantIdentity.variantId);
          if (window.BizDecksCartIdentityRegister) window.BizDecksCartIdentityRegister(variantIdentity);
        } else {
          formData.set('properties[_bd_gift_card]', 'true');
        }

        /* BizDecks V75 normalize cart properties.
           Shopify only merges cart lines when variant, selling plan and properties match exactly.
           Remove unstable tracking properties that create duplicate physical/digital lines.
        */
        ['properties[_timestamp]', 'properties[_source]', 'properties[_cart_uid]', 'properties[_random]', 'properties[_added_from]'].forEach((key) => {
          if (formData.has(key)) formData.delete(key);
        });

        const normalizeBizDecksKey = (value) =>
          String(value || '')
            .toLowerCase()
            .replace(/&/g, 'and')
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');

        const inferBizDecksPackageKey = () => {
          const existing = normalizeBizDecksKey(
            formData.get('properties[_bd_package_key]') ||
              formData.get('properties[package_key]') ||
              formData.get('properties[Package]')
          );
          const sourceText = normalizeBizDecksKey(
            [
              existing,
              this.form.dataset.bdPackageKey,
              this.form.dataset.packageKey,
              this.dataset.bdPackageKey,
              this.dataset.packageKey,
              this.closest('[data-bd-package-key]')?.dataset.bdPackageKey,
              this.closest('[data-product-title]')?.dataset.productTitle,
              document.querySelector('[data-bd-selected-product-title]')?.textContent,
              document.querySelector('h1')?.textContent,
              window.location.pathname
            ].filter(Boolean).join(' ')
          );

          if (/complete|full_package|bundle/.test(sourceText)) return 'complete';
          if (/google|sheet|sheets|workbook|green/.test(sourceText)) return 'sheets';
          if (/miro|whiteboard|board|yellow/.test(sourceText)) return 'miro';
          if (/digital_all|digital_cards|digital_card|digital_only|online|red/.test(sourceText)) return 'digital_all';
          if (/physical|strategy_deck|card_deck|cards_box|blue/.test(sourceText) && !/digital/.test(sourceText)) return 'physical';
          return existing;
        };

        const cartMerge = window.BizDecksCartMerge;
        let bizDecksPackageKey = inferBizDecksPackageKey();
        if (cartMerge && typeof cartMerge.packageKey === 'function') {
          bizDecksPackageKey = cartMerge.packageKey(bizDecksPackageKey);
        }
        if (bizDecksPackageKey) {
          const stableProps = isGiftCard
            ? {
              _bd_controlled_product: 'true',
              _bd_package_key: bizDecksPackageKey,
              _bd_seat_product: 'false'
            }
            : cartMerge && typeof cartMerge.identityProperties === 'function'
            ? cartMerge.identityProperties({
              sku: variantIdentity.sku,
              variantId: variantIdentity.variantId,
              packageKey: bizDecksPackageKey,
              purchaseMode: formData.get('properties[_bd_purchase_mode]')
            })
            : cartMerge && typeof cartMerge.stableProperties === 'function'
              ? cartMerge.stableProperties(bizDecksPackageKey, variantIdentity)
            : {
              _bd_controlled_product: 'true',
              _bd_package_key: bizDecksPackageKey,
              _bd_seat_product: bizDecksPackageKey === 'physical' ? 'false' : 'true'
            };
          Object.entries(stableProps).forEach(([name, value]) => {
            if (value != null && value !== '') formData.set(`properties[${name}]`, value);
          });
        }

        let sectionIds = ['cart-drawer', 'cart-icon-bubble'];
        try {
          if (typeof this.cart?.getSectionsToRender === 'function') {
            const ids = this.cart.getSectionsToRender().map(section => section.id).filter(id => typeof id === 'string' && id);
            if (ids.length) sectionIds = ids;
          }
          if (typeof this.cart?.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
        } catch (_) { /* Cart display preparation cannot prevent the product mutation. */ }
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
        config.body = formData;


          const deliveryCheck = window.BizDecksCartGuardValidateItems?.([{
            id: formData.get('id'),
            properties: { _bd_package_key: formData.get('properties[_bd_package_key]') }
          }], this.submitButton);
          if (deliveryCheck && !deliveryCheck.ok) {
            this.safeCartMessage(deliveryCheck.message);
            return;
          }

          const quickAddModal = this.closest('quick-add-modal');
          const cartTrigger = quickAddModal?.openedBy || this.submitButton;
          // Closing first releases the modal focus trap before the cart shell opens.
          try { if (typeof quickAddModal?.hide === 'function') quickAddModal.hide(true); } catch (_) {}
          const support = window.BizDecksCartSupport;
          const request = support && typeof support.request === 'function' ? support.request.bind(support) : fetch;
          mutationSent = true;
          const httpResponse = await request(`${routes.cart_add_url}`, config);
          confirmedAdd = httpResponse.bdCartAddConfirmed === true;
          let response;
          try { response = await httpResponse.json(); }
          catch (error) { throw new Error('Cart response could not be read.'); }
          if (!httpResponse.ok || Number(response?.status) >= 400) {
            receivedRejection = true;
            const shopifyMessage = response?.description || response?.message || window.cartStrings?.error || 'This item could not be added.';
            const definiteAvailability = this.definitelyUnavailable(httpResponse, response, shopifyMessage);
            const physicalBlocked = response?.physicalDeliveryBlocked || httpResponse.bdCartValidation?.physicalDeliveryBlocked;
            const unknownOutcome = httpResponse.bdCartOutcome === 'unknown' || Number(httpResponse.status) >= 500;
            const partialPossible = httpResponse.bdCartPartialPossible === true || unknownOutcome || (!physicalBlocked && Number(httpResponse.status || response?.status) === 422 && !definiteAvailability);
            const errorMessage = shopifyMessage + (partialPossible ? ' Review your cart before adding again.' : '');
            this.safeCartMessage(errorMessage);
            if (response?.physicalDeliveryBlocked || httpResponse.bdCartValidation?.physicalDeliveryBlocked) return;
            try {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form', productVariantId: formData.get('id'),
                errors: response?.errors || response?.description, message: response?.message,
              });
            } catch (_) { /* Subscribers cannot change Shopify's known rejection. */ }
            const rejected = new Error('Cart add rejected');
            if (unknownOutcome) rejected.name = 'CartUncertainError';
            this.reportCartFeedback(rejected, {message:errorMessage,record:false});
            if (httpResponse.bdCartPartialPossible === true) {
              const controller = this.readyCartController();
              if (controller) {
                try { await controller.refresh({open:true,trigger:cartTrigger,sections:response?.sections,validation:httpResponse.bdCartValidation}); } catch (_) {}
              }
            }
            if (definiteAvailability) {
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButtonText?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
              }
            }
            return;
          }
          if (!confirmedAdd && !(response && typeof response === 'object' && (response.id != null || (Array.isArray(response.items) && response.items.length > 0 && response.items.every(item => item && (item.id != null || item.variant_id != null)))))) throw new Error('Cart add could not be confirmed.');
          confirmedAdd = true;
          actionRef = httpResponse.bdCartActionRef || httpResponse.bdCartValidation?.action_ref || null;
          const state = Object.assign({}, response, {bdCartValidation:httpResponse.bdCartValidation});
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, {source:'product-form',productVariantId:formData.get('id'),cartData:state});
          } catch (_) { /* The accepted add remains accepted when a subscriber fails. */ }
          const controller = this.readyCartController();
          if (controller) {
            await controller.refresh({open:true,trigger:cartTrigger,sections:state.sections,validation:state.bdCartValidation});
          } else if (typeof this.cart?.renderContents === 'function' && sectionIds.every(id => typeof state.sections?.[id] === 'string' && state.sections[id])) {
            const rendered = await this.cart.renderContents(state);
            if (rendered === false) this.goToFullCart();
          } else this.goToFullCart();
        } catch (error) {
          if (error?.cartMutationConfirmed === true) confirmedAdd = true;
          const message = confirmedAdd
            ? 'Added to your cart. Open your cart to review it.'
            : receivedRejection
              ? window.cartStrings?.error || 'This item could not be added.'
              : mutationSent
                ? 'We could not confirm the cart update. Review your cart before trying again.'
                : window.cartStrings?.error || 'Something went wrong. Please try again.';
          this.safeCartMessage(message);
          try {
            const monitor = window.BizDecksErrorMonitor;
            let recorded;
            if (confirmedAdd && !this.readyCartController()) recorded = monitor?.captureVisibleError?.({error_code:'PRODUCT_ADD_UI_FAILED',interaction:'add_to_cart',action_ref:actionRef,request_endpoint:'/cart/add.js'});
            else if (!window.BizDecksCart && error?.name !== 'CartUncertainError') recorded = monitor?.captureVisibleError?.({error_code:'CART_ADD_REJECTED',interaction:'add_to_cart',request_endpoint:'/cart/add.js'});
            recorded?.catch?.(() => {});
          } catch (_) {}
          this.reportCartFeedback(error, {message,record:false,mutationConfirmed:confirmedAdd});
          if (confirmedAdd) this.goToFullCart();
          console.error(error);
        } finally {
          this.submitting = false;
          this.submitButton.classList.remove('loading');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      }

      readyCartController() {
        try {
          const cart = window.BizDecksCart;
          return typeof cart?.isReady === 'function' && cart.isReady() === true && typeof cart.refresh === 'function' ? cart : null;
        } catch (_) { return null; }
      }

      safeCartMessage(message) {
        try { this.handleErrorMessage(message); } catch (_) {}
      }

      reportCartFeedback(error, options) {
        try {
          const result = window.BizDecksCart?.reportError?.(error, options);
          result?.catch?.(() => {});
        } catch (_) {}
      }

      goToFullCart() {
        let route = window.Shopify?.routes?.root || '/';
        route = route.replace(/\/?$/, '/') + 'cart';
        try {
          const value = window.routes?.cart_url;
          if (typeof value === 'string' && /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?cart\/?$/.test(value)) route = value;
          if (typeof window.location.assign === 'function') window.location.assign(route);
          else window.location.href = route;
        } catch (_) { window.location.href = route; }
      }

      definitelyUnavailable(httpResponse, response, message) {
        if (Number(httpResponse.status || response?.status) !== 422 || /quantity|maximum|limit|only \d|more than|already have|not sold out|not out of stock/i.test(message)) return false;
        return /^(?:this (?:product|variant|item) is (?:currently )?(?:sold out|out of stock)\.?|the product .+ is (?:already )?sold out\.?)$/i.test(String(message).trim());
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;


        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          if (typeof this.errorMessageWrapper.setAttribute === 'function') this.errorMessageWrapper.setAttribute('data-bd-monitor-owned', 'true');
          if (typeof this.errorMessage?.setAttribute === 'function') this.errorMessage.setAttribute('data-bd-monitor-owned', 'true');
          this.errorMessage.textContent = errorMessage;
        }
      }
      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton || !this.submitButtonText) {
          return;
        }
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }
      
      get variantIdInput() {
        // return this.form.querySelector('[name=id]');
        return this.form ? this.form.querySelector('[name=id]') : null;
      }
    }
  );
}
